import { type LinkProps, type ReactNode } from '@tanstack/react-router';
import { ArrowLeft, Info, ListCheck, type LucideIcon, Map, MessagesSquare, Settings, Trophy } from 'lucide-react';
import type { FC } from 'react';
import { Spacer } from './Spacer';
import { useStore, useUnreadChatMessageCount } from 'engine/store';
import { Icon } from './Icon';
import { useLocation } from '@tanstack/react-router';
import { useRouter } from '@tanstack/react-router';
import { navigateBack, useOnline } from 'engine/api';
import { useEffect, useState } from 'react';
import { NotificationBadge } from './NotificationBadge';
import { DeviceOrientationEventWithPermission } from 'engine/geoLocationAsync';
import { appConfig } from 'engine/appConfig';

const bottomLinkUrls = ['/rounds', '/leaderboard', '/help', '/map', '/chat'];

const useMdBreakpoint = () => {
	const mdBreakpoint = 768;

	const [isMdOrLarger, setIsMdOrLarger] = useState(window.innerWidth >= mdBreakpoint);

	useEffect(() => {
		const handleResize = () => {
			setIsMdOrLarger(window.innerWidth >= mdBreakpoint);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	return isMdOrLarger;
};

export const GameShell: FC<{ children?: ReactNode }> = ({ children }) => {
	const game = useStore((s) => s.game);
	const playState = useStore((s) => s.playState);

	const location = useLocation();

	const isFullscreenVideo = location.pathname.startsWith('/fullscreen-video');
	const isARScene = location.pathname.startsWith('/ar-scene');

	// Define which routes should show the TopNav
	const routesWithoutNav = ['/login', '/about'];
	const shouldShowNav = game && !routesWithoutNav.includes(location.pathname) && !isFullscreenVideo && !isARScene;

	// If score, map and chat are all disabled we don't need to show the bottom nav
	const allNavItemsDisabled = game?.hideScore && !game?.showMap && !game?.gameMasterChat && !game?.globalChat;
	const shouldShowBottomNav = shouldShowNav && !allNavItemsDisabled && playState === 'playing';

	return (
		<div id="game-shell-container" className={`flex h-screen flex-col skin-bg-img skin-text skin-text-font`}>
			{shouldShowNav && <TopNav />}
			<div className="flex h-0 flex-1 shrink flex-col md:flex-row-reverse">
				<div className="flex-1 shrink-0 overflow-auto [view-transition-name:main-content]">{children}</div>
				{shouldShowBottomNav && <BottomNav />}
			</div>
			<OfflineBanner />
		</div>
	);
};

const OfflineBanner = () => {
	const isOnline = useOnline();
	const isTrackingLocation = useStore((s) => s.playerLocation.isTracking);
	const game = useStore((s) => s.game);

	const [locationAccess, setLocationAccess] = useState(false);

	if (!isOnline)
		return <div className="rounded-t-lg bg-red-500 px-3 py-1 text-sm">{__`You are offline. Some features may not work.`}</div>;

	if (appConfig.permissions.allowGps) {
		if (navigator?.permissions?.query) {
			navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
				permissionStatus.onchange = () => {
					setLocationAccess(permissionStatus.state == 'granted');
				};
			});
		}

		if (!isTrackingLocation && game?.showMap && !locationAccess) {
			// const handleClick = () => {
			// 	console.log('DETECTING PERMISSIONS REQUESTING');
			// 	if (typeof DeviceOrientationEventWithPermission.requestPermission === 'function') {
			// 		DeviceOrientationEventWithPermission.requestPermission();
			// 	}
			// 	router.navigate({ to: '/map', viewTransition: { types: ['slide-right'] } });
			// };
			return (
				<div
					// onClick={handleClick}
					className="rounded-t-lg bg-red-500 px-3 py-1 text-sm"
				>{__`Unable to track location; tracking is required to play the game`}</div>
			);
		}
	}
};

const NavLink: FC<{
	to?: LinkProps['to'];
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
	icon: LucideIcon;
	notificationCount?: number;
}> = ({ icon, to, notificationCount, onClick }) => {
	const currentUrl = useLocation().pathname;
	const currentIndex = bottomLinkUrls.findIndex((url) => currentUrl.startsWith(url));
	const targetIndex = bottomLinkUrls.indexOf(to!);
	const isMdOrLarger = useMdBreakpoint();
	const router = useRouter();

	const location = useLocation();
	let viewTransition: string = 'slide-down';
	if (currentIndex > -1) {
		if (targetIndex > currentIndex) {
			viewTransition = 'slide-left';
		} else {
			viewTransition = 'slide-right';
		}
	}

	if (to != undefined && onClick == undefined) {
		const transition = isMdOrLarger ? undefined : { types: [viewTransition] };
		onClick = () => router.navigate({ to: to, viewTransition: transition });
	}
	// console.log(location.pathname);

	return (
		<button
			// to={to}
			onClick={onClick}
			className="group rounded-md p-4"
		>
			<div className="relative">
				<Icon icon={icon} className="size-6" />
				<div
					className={`absolute mt-1 h-1 w-0 bg-[var(--skin-text)] transition-all duration-100 skin-rounded-full group-data-[status=active]:w-full ${location.pathname == to ? 'w-full' : ''}`}
				></div>
				{(notificationCount ?? 0) > 0 && <NotificationBadge count={notificationCount ?? 0} />}
			</div>
		</button>
	);
};

function TopNav() {
	const location = useLocation();
	const router = useRouter();

	const handleBack = () => {
		// Define back button behavior based on current route
		switch (location.pathname) {
			case '/rounds':
			case '/current-task':
			case '/current-round':
			case '/intro':
			case '/register-members':
			case '/showing-rat':
				break;
			case '/leaderboard':
			case '/help':
			case '/map':
			case '/chat':
				router.navigate({ to: '/rounds', viewTransition: { types: ['slide-right'] } });
				break;
			default: {
				const transition = location.pathname.includes('/settings') ? 'slide-down' : 'slide-right';
				navigateBack(router, transition);
			}
		}
	};

	return (
		<div className="skin-bg safe-area-t safe-area-h">
			<div className="flex px-2">
				{!['/rounds', '/current-task', '/current-round', '/register-members', '/showing-rat', '/intro'].includes(
					location.pathname,
				) && (
					<button onClick={handleBack} className="p-4">
						<ArrowLeft />
					</button>
				)}
				<Spacer />
				<button
					className="p-4"
					onClick={() => {
						if (location.pathname === '/settings') {
							navigateBack(router, 'slide-down');
						} else {
							router.navigate({ to: '/settings', viewTransition: { types: ['slide-up'] } });
						}
					}}
				>
					<Settings />
				</button>
			</div>
		</div>
	);
}

function BottomNav() {
	// NOTE: BottomNav should only be rendered if a game is loaded.
	const game = useStore((s) => s.game)!;
	const taskStates = useStore((s) => s.taskStates)!;
	const roundStates = useStore((s) => s.roundStates)!;

	const { unreadCount } = useUnreadChatMessageCount();
	const router = useRouter();
	const handleMapClick = () => {
		console.log('DETECTING PERMISSIONS REQUESTING');
		if (typeof DeviceOrientationEventWithPermission.requestPermission === 'function') {
			DeviceOrientationEventWithPermission.requestPermission();
		}
		router.navigate({ to: '/map', viewTransition: { types: ['slide-right'] } });
	};

	const tasksTodoMap =
		Object.entries(taskStates).filter(([_key, state]) => {
			const task = game.rounds.find((r) => r.tasks.some((t) => t.id == state.taskId))?.tasks.find((t) => t.id == state.taskId);
			if (!task || task.availabilityGpsConfig?.showCoordinates == false) return false;
			return !state.unlocked && task.availability == 'gps';
		}).length || 0;

	const roundsTodoMap =
		Object.entries(roundStates).filter(([_key, state]) => {
			const round = game.rounds.find((r) => r.id == state.roundId);
			if (!round || round.availabilityGpsConfig?.showCoordinates == false) return false;
			return !state.unlocked && round.availability == 'gps';
		}).length || 0;

	const tasksTodo =
		Object.entries(roundStates).filter(([_key, state]) => {
			const round = game.rounds.find((r) => r.id == state.roundId);
			if (!round || !roundStates[round.id].unlocked) return false;
			return !round.tasks.every((t) => ['done', 'in-progress'].includes(taskStates[t.id].status));
		}).length || 0;

	return (
		<div className="skin-bg-img safe-area-b safe-area-h [view-transition-name:bottom-nav]">
			<div className="relative z-10 px-6 py-2 md:px-2">
				<div className="mx-auto flex w-full max-w-[400px] justify-between md:flex-col">
					<NavLink to="/rounds" icon={ListCheck} notificationCount={tasksTodo} />
					{!game.hideScore && <NavLink to="/leaderboard" icon={Trophy} />}
					<NavLink to="/help" icon={Info} />
					{appConfig.permissions.allowGps && game.showMap && (
						<NavLink onClick={handleMapClick} icon={Map} to="/map" notificationCount={tasksTodoMap + roundsTodoMap} />
					)}
					{(game.gameMasterChat || game.globalChat) && (
						<NavLink notificationCount={unreadCount} to="/chat" icon={MessagesSquare} />
					)}
				</div>
			</div>
		</div>
	);
}
